import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Container,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  FormControl,
  Select,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
} from '@mui/material';
import {
  Speed as SpeedIcon,
  Assignment as ExamIcon,
  EventAvailable as AttendanceIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as TrophyIcon,
  CalendarToday as CalendarIcon,
  Today as TodayIcon,
  DateRange as DateRangeIcon,
  AllInclusive as AllTimeIcon,
  AccountBalanceWallet as WalletIcon,
  Insights as InsightsIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  ExitToApp as LogoutIcon,
  Menu as MenuIcon,
  History as HistoryIcon,
  School as SchoolIcon,
  Book as BookIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import Drawer from '@mui/material/Drawer';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadialBarChart,
  RadialBar,
} from 'recharts';
import axios from 'axios';
import { toast } from 'react-toastify';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#00f2fe'];

const toNumber = (value) => Number.parseFloat(value ?? 0) || 0;

const formatCurrency = (value) => `₹${toNumber(value).toLocaleString('en-IN')}`;

const getDaysSince = (dateValue) => {
  if (!dateValue) return 0;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return 0;
  const diff = Date.now() - date.getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
};

const StudentStatistics = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statistics, setStatistics] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [daysFilter, setDaysFilter] = useState(30);
  const [leaderboardStats, setLeaderboardStats] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuOpen = Boolean(anchorEl);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Lesson Progress
  const [lessonProgress, setLessonProgress] = useState(null);
  const [selectedCourseForProgress, setSelectedCourseForProgress] = useState('');
  const [enrolledCourses, setEnrolledCourses] = useState([]);

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('student_token');
    if (!token) {
      navigate('/student/login');
      return;
    }

    loadStudentData();
    loadStatistics();
    loadLeaderboardStats();
    loadLessonProgress();
  }, [daysFilter, navigate]);

  const loadStudentData = async () => {
    try {
      const studentDataStr = localStorage.getItem('student_data');
      if (studentDataStr) {
        const cachedData = JSON.parse(studentDataStr);

        // Refresh from the public dashboard route so fee fields stay available.
        const phone = cachedData.phone;
        if (phone) {
          try {
            const response = await axios.get(`${API_BASE_URL}/students/dashboard/${phone}`);
            if (response.data.success) {
              const student = response.data.student;
              setStudentData(student);
              localStorage.setItem('student_data', JSON.stringify(student));
              return;
            }
          } catch (refreshError) {
            console.error('Error refreshing student dashboard data:', refreshError);
          }
        }

        setStudentData(cachedData);

        // Load enrolled courses
        const courses = cachedData.enrolled_courses || [];
        const normalizedCourses = courses.map(c => String(c).toUpperCase().trim());
        setEnrolledCourses(normalizedCourses);
        
        // Set default course for progress
        if (normalizedCourses.length > 0 && !selectedCourseForProgress) {
          setSelectedCourseForProgress(normalizedCourses[0]);
        }
      }
    } catch (error) {
      console.error('Error loading student data:', error);
    }
  };

  const loadLessonProgress = async () => {
    try {
      const token = localStorage.getItem('student_token');
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/track-sheets/lesson-progress`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setLessonProgress(response.data.progress);
      }
    } catch (err) {
      console.error('Error loading lesson progress:', err);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('student_token');
    localStorage.removeItem('student_data');
    toast.success('Logged out successfully');
    navigate('/student/login');
  };

  const handleViewHistory = () => {
    navigate('/student/track-sheet/history');
  };

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('student_token');
      
      if (!token) {
        setError('Please login to view statistics');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/track-sheets/statistics`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { days: daysFilter },
      });

      if (response.data.success) {
        setStatistics(response.data.statistics);
      } else {
        setError(response.data.message || 'Failed to load statistics');
      }
    } catch (err) {
      console.error('Error loading statistics:', err);
      setError(err.response?.data?.message || 'Failed to load statistics');
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderboardStats = async () => {
    try {
      const token = localStorage.getItem('student_token');
      if (!token) return;

      // Get student phone from localStorage
      const studentDataStr = localStorage.getItem('student_data');
      let studentPhone = null;
      if (studentDataStr) {
        try {
          const studentData = JSON.parse(studentDataStr);
          studentPhone = studentData.phone;
        } catch (e) {
          console.error('Error parsing student data:', e);
        }
      }

      // Get today's date
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch leaderboard statistics
      const response = await axios.get(`${API_BASE_URL}/track-sheets/leaderboard-stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { studentPhone },
      });

      if (response.data.success) {
        setLeaderboardStats(response.data.stats);
      }
    } catch (err) {
      console.error('Error loading leaderboard stats:', err);
      // Don't show error toast for this, it's optional
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  const getFeeSummary = () => {
    const totalFee = toNumber(studentData?.total_fee);
    const paidFee = toNumber(studentData?.paid_fee);
    const dueFee = toNumber(studentData?.due_fee);
    const paidPercent = totalFee > 0 ? Math.min(100, (paidFee / totalFee) * 100) : 0;

    return {
      totalFee,
      paidFee,
      dueFee,
      paidPercent,
      isPaid: dueFee <= 0,
    };
  };

  const getAttendanceInsights = () => {
    const total = statistics?.attendance?.length || 0;
    const present = statistics?.attendance?.filter((item) => item.status === 'present').length || 0;
    const absent = statistics?.attendance?.filter((item) => item.status === 'absent').length || 0;
    const late = statistics?.attendance?.filter((item) => item.status === 'late').length || 0;
    const attendancePercentage = total > 0 ? (present / total) * 100 : 0;

    return {
      total,
      present,
      absent,
      late,
      attendancePercentage,
    };
  };

  const getRecentAttendance = () => {
    return [...(statistics?.attendance || [])]
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
      .slice(0, 5);
  };

  const getReportRangeLabel = () => {
    if (daysFilter === 7) return 'Last 7 days';
    if (daysFilter === 30) return 'Last 30 days';
    if (daysFilter === 90) return 'Last 90 days';
    return `${daysFilter} days`;
  };

  // Prepare data for typing practice chart
  const prepareSpeedChartData = () => {
    if (!statistics?.speedPractice?.byType) return [];
    
    const allDates = new Set();
    Object.values(statistics?.speedPractice?.byType || {}).forEach(typeData => {
      typeData.forEach(item => allDates.add(item.date));
    });

    const dates = Array.from(allDates).sort();
    const chartData = dates.map(date => {
      const dataPoint = { date: formatDate(date) };
      Object.keys(statistics.speedPractice.byType).forEach(type => {
        const typeData = statistics.speedPractice.byType[type];
        const entry = typeData.find(item => item.date === date);
        dataPoint[type] = entry ? entry.speed : null;
      });
      return dataPoint;
    });

    return chartData;
  };

  // Prepare data for exam results chart
  const prepareExamChartData = () => {
    if (!statistics?.examResults) return [];
    
    return statistics.examResults.map(exam => ({
      date: formatDate(exam.date),
      examName: exam.examName,
      percentage: exam.percentage,
      obtainedMarks: exam.obtainedMarks,
      totalMarks: exam.totalMarks,
    }));
  };

  // Prepare attendance data
  const prepareAttendanceData = () => {
    if (!statistics?.attendance || statistics.attendance.length === 0) {
      return {
        present: 0,
        absent: 0,
        late: 0,
        total: 0,
        percentage: 0,
      };
    }

    const present = statistics.attendance.filter(a => a.status === 'present').length;
    const absent = statistics.attendance.filter(a => a.status === 'absent').length;
    const late = statistics.attendance.filter(a => a.status === 'late').length;
    const total = statistics.attendance.length;
    const percentage = total > 0 ? (present / total) * 100 : 0;

    return { present, absent, late, total, percentage };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!statistics) {
    return (
      <Alert severity="info">
        No statistics data available. Start tracking your progress to see statistics!
      </Alert>
    );
  }

  const speedChartData = prepareSpeedChartData();
  const examChartData = prepareExamChartData();
  const attendanceData = prepareAttendanceData();
  const attendanceInsights = getAttendanceInsights();
  const feeSummary = getFeeSummary();
  const recentAttendance = getRecentAttendance();
  const registrationDays = getDaysSince(studentData?.joining_date || studentData?.created_at);
  const attendancePieData = [
    { name: 'Present', value: attendanceData.present, color: '#4CAF50' },
    { name: 'Absent', value: attendanceData.absent, color: '#F44336' },
    { name: 'Late', value: attendanceData.late, color: '#FF9800' },
  ].filter(item => item.value > 0);

  const renderLeaderboardCards = (category) => {
    const stats = leaderboardStats?.[category];
    if (!stats) return null;

    const getTitle = () => {
      if (category === 'speed') return '🏆 Speed Records';
      if (category === 'exams') return '📝 Exam Results';
      if (category === 'attendance') return '📅 Attendance Records';
      return '🏆 Records';
    };

    const getValue = (period) => {
      if (category === 'speed') {
        if (period === 'today') return stats.todayWinner?.highestSpeed || 0;
        if (period === 'week') return stats.weekHighest?.highestSpeed || 0;
        if (period === 'month') return stats.monthHighest?.highestSpeed || 0;
        if (period === 'allTime') return stats.allTimeHighest?.highestSpeed || 0;
      }
      if (category === 'exams') {
        if (period === 'today') return stats.todayWinner?.percentage || 0;
        if (period === 'week') return stats.weekHighest?.percentage || 0;
        if (period === 'month') return stats.monthHighest?.percentage || 0;
        if (period === 'allTime') return stats.allTimeHighest?.percentage || 0;
      }
      if (category === 'attendance') {
        if (period === 'today') return stats.todayWinner?.presentDays || 0;
        if (period === 'week') return stats.weekHighest?.presentDays || 0;
        if (period === 'month') return stats.monthHighest?.presentDays || 0;
        if (period === 'allTime') return stats.allTimeHighest?.presentDays || 0;
      }
      return 0;
    };

    const getUnit = () => {
      if (category === 'speed') return ' WPM';
      if (category === 'exams') return '%';
      if (category === 'attendance') return ' days';
      return '';
    };

    const getName = (period) => {
      if (period === 'today') return stats.todayWinner?.studentName || 'No record today';
      if (period === 'week') return stats.weekHighest?.studentName || 'No record this week';
      if (period === 'month') return stats.monthHighest?.studentName || 'No record this month';
      if (period === 'allTime') return stats.allTimeHighest?.studentName || 'No record';
      return 'N/A';
    };

    const getSubtext = (period) => {
      if (period === 'today') return 'Today\'s highest';
      if (period === 'week') return 'Week\'s highest (7 days)';
      if (period === 'month') return 'Month\'s highest (30 days)';
      if (period === 'allTime') return 'All time record';
      return '';
    };

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrophyIcon sx={{ fontSize: 40 }} />
                <Typography variant="h5" fontWeight="bold">
                  {getTitle()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Today Winner */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#FFD700', color: 'white', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TodayIcon />
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                  Today Topper
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {getValue('today')}{getUnit()}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }} fontWeight="medium">
                {getName('today')}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                {getSubtext('today')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Week Highest */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#4CAF50', color: 'white', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <DateRangeIcon />
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                  Week Highest
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {getValue('week')}{getUnit()}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }} fontWeight="medium">
                {getName('week')}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                {getSubtext('week')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Month Highest */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#2196F3', color: 'white', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarIcon />
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                  Month Highest
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {getValue('month')}{getUnit()}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }} fontWeight="medium">
                {getName('month')}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                {getSubtext('month')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* All Time Highest */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#FF9800', color: 'white', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AllTimeIcon />
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                  All Time Highest
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {getValue('allTime')}{getUnit()}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }} fontWeight="medium">
                {getName('allTime')}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                {getSubtext('allTime')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Professional Navigation Bar - Responsive */}
      <AppBar position="sticky" sx={{ bgcolor: 'primary.main', boxShadow: 3 }}>
        <Toolbar>
          <Container maxWidth="xl" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', px: { xs: 1, sm: 2 } }}>
            {/* Logo/Brand */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={() => setMobileMenuOpen(true)}
                sx={{ display: { xs: 'block', md: 'none' }, mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
              <SchoolIcon sx={{ fontSize: { xs: 24, sm: 32 } }} />
              <Typography variant="h6" fontWeight="bold" sx={{ display: { xs: 'none', sm: 'block' }, fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                KNLS Student Portal
              </Typography>
            </Box>

            {/* Navigation Links - Desktop */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center', flex: 1, justifyContent: 'center' }}>
              <Button
                color="inherit"
                startIcon={<HomeIcon />}
                onClick={() => navigate('/student/dashboard')}
                sx={{
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                  fontWeight: location.pathname === '/student/dashboard' ? 'bold' : 'normal',
                }}
              >
                Home
              </Button>
              <Button
                color="inherit"
                startIcon={<ExamIcon />}
                onClick={() => navigate('/student/track-sheet')}
                sx={{
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                  fontWeight: location.pathname === '/student/track-sheet' ? 'bold' : 'normal',
                }}
              >
                Track Sheet
              </Button>
              <Button
                color="inherit"
                startIcon={<HistoryIcon />}
                onClick={handleViewHistory}
                sx={{
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                History
              </Button>
              <Button
                color="inherit"
                startIcon={<TrophyIcon />}
                onClick={() => navigate('/student/statistics')}
                sx={{
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                  fontWeight: location.pathname === '/student/statistics' ? 'bold' : 'normal',
                  bgcolor: location.pathname === '/student/statistics' ? 'rgba(255,255,255,0.15)' : 'transparent',
                }}
              >
                Statistics
              </Button>
            </Box>

            {/* User Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
              <Typography variant="body2" sx={{ display: { xs: 'none', lg: 'block' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {studentData?.name || 'Student'}
              </Typography>
              <IconButton
                onClick={handleMenuOpen}
                sx={{ color: 'white' }}
                size="small"
              >
                <Avatar 
                  sx={{ 
                    width: { xs: 28, sm: 32, md: 36 }, 
                    height: { xs: 28, sm: 32, md: 36 }, 
                    bgcolor: 'secondary.main' 
                  }}
                  src={studentData?.photo_url || studentData?.photo}
                >
                  {studentData?.photo_url || studentData?.photo ? null : (studentData?.name?.charAt(0)?.toUpperCase() || <PersonIcon />)}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => { navigate('/student/dashboard'); handleMenuClose(); }}>
                  <PersonIcon sx={{ mr: 1 }} /> Profile
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { handleLogout(); handleMenuClose(); }}>
                  <LogoutIcon sx={{ mr: 1 }} /> Logout
                </MenuItem>
              </Menu>
            </Box>
          </Container>
        </Toolbar>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: 'primary.main',
            color: 'white',
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
          <Avatar 
            sx={{ 
              width: 56, 
              height: 56, 
              bgcolor: 'secondary.main' 
            }}
            src={studentData?.photo_url || studentData?.photo}
          >
            {studentData?.photo_url || studentData?.photo ? null : (studentData?.name?.charAt(0)?.toUpperCase() || <PersonIcon />)}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {studentData?.name || 'Student'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {studentData?.student_id || ''}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            color="inherit"
            startIcon={<HomeIcon />}
            onClick={() => { navigate('/student/dashboard'); setMobileMenuOpen(false); }}
            sx={{
              justifyContent: 'flex-start',
              mb: 0.5,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            Home
          </Button>
          <Button
            fullWidth
            color="inherit"
            startIcon={<ExamIcon />}
            onClick={() => { navigate('/student/track-sheet'); setMobileMenuOpen(false); }}
            sx={{
              justifyContent: 'flex-start',
              mb: 0.5,
              bgcolor: location.pathname === '/student/track-sheet' ? 'rgba(255,255,255,0.15)' : 'transparent',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            Track Sheet
          </Button>
          <Button
            fullWidth
            color="inherit"
            startIcon={<HistoryIcon />}
            onClick={() => { handleViewHistory(); setMobileMenuOpen(false); }}
            sx={{
              justifyContent: 'flex-start',
              mb: 0.5,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            History
          </Button>
          <Button
            fullWidth
            color="inherit"
            startIcon={<TrophyIcon />}
            onClick={() => { navigate('/student/statistics'); setMobileMenuOpen(false); }}
            sx={{
              justifyContent: 'flex-start',
              mb: 0.5,
              bgcolor: location.pathname === '/student/statistics' ? 'rgba(255,255,255,0.15)' : 'transparent',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            Statistics
          </Button>
          <Divider sx={{ my: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
          <Button
            fullWidth
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
            sx={{
              justifyContent: 'flex-start',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            Logout
          </Button>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ py: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3, px: { xs: 2, sm: 3 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.05) 100%)',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Student Statistics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {studentData?.name || 'Student'} • {getReportRangeLabel()} • {registrationDays} days since registration
                </Typography>
              </Box>

              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
                <InputLabel id="days-filter-label">Report Range</InputLabel>
                <Select
                  labelId="days-filter-label"
                  value={daysFilter}
                  label="Report Range"
                  onChange={(event) => setDaysFilter(Number(event.target.value))}
                >
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={90}>Last 90 days</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>
        </Box>

        {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SpeedIcon />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {statistics?.summary?.totalSpeedPractices || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Typing Practices
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: 'secondary.main', color: 'white' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ExamIcon />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {statistics?.summary?.totalExams || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Exams Taken
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttendanceIcon />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {attendanceData.percentage.toFixed(0)}%
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Attendance
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {statistics?.summary?.averageExamPercentage?.toFixed(1) || 0}%
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Avg Exam Score
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for different statistics */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            variant={isMobile ? 'scrollable' : 'fullWidth'}
            scrollButtons="auto"
          >
            <Tab icon={<SpeedIcon />} iconPosition="start" label="Speed Records" />
            <Tab icon={<ExamIcon />} iconPosition="start" label="Exam Results" />
            <Tab icon={<AttendanceIcon />} iconPosition="start" label="Attendance" />
            <Tab icon={<WalletIcon />} iconPosition="start" label="Fees & Insights" />
            <Tab icon={<BookIcon />} iconPosition="start" label="Lesson Progress" />
          </Tabs>
        </Box>

        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Speed Records Tab */}
          {selectedTab === 0 && (
            <Box>
              {renderLeaderboardCards('speed')}
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                Typing Speed Progress
              </Typography>
              {speedChartData.length === 0 ? (
                <Alert severity="info">
                  No typing practice data available. Start practicing to see your progress!
                </Alert>
              ) : (
                <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
                  <LineChart data={speedChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis label={{ value: 'Speed (WPM)', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Legend />
                    {statistics?.speedPractice?.byType && Object.keys(statistics.speedPractice.byType).map((type, index) => (
                      <Line
                        key={type}
                        type="monotone"
                        dataKey={type}
                        stroke={COLORS[index % COLORS.length]}
                        strokeWidth={2}
                        name={type.charAt(0).toUpperCase() + type.slice(1)}
                        dot={{ r: 4 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Box>
          )}

          {/* Exam Results Tab */}
          {selectedTab === 1 && (
            <Box>
              {renderLeaderboardCards('exams')}
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                Exam Performance
              </Typography>
              {examChartData.length === 0 ? (
                <Alert severity="info">
                  No exam results available. Complete exams to see your performance!
                </Alert>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
                    <BarChart data={examChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }} />
                      <Tooltip
                        formatter={(value, name) => {
                          if (name === 'percentage') return [`${value}%`, 'Percentage'];
                          return [value, name];
                        }}
                      />
                      <Legend />
                      <Bar dataKey="percentage" fill="#667eea" name="Percentage" />
                    </BarChart>
                  </ResponsiveContainer>
                  
                  {/* Exam Details Table */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Exam Details
                    </Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f5f5f5' }}>
                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Date</th>
                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Exam</th>
                            <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Marks</th>
                            <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Percentage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {examChartData.map((exam, index) => (
                            <tr key={index}>
                              <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{exam.date}</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{exam.examName}</td>
                              <td style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                                {exam.obtainedMarks}/{exam.totalMarks}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                                <Chip
                                  label={`${exam.percentage.toFixed(1)}%`}
                                  size="small"
                                  color={exam.percentage >= 80 ? 'success' : exam.percentage >= 60 ? 'warning' : 'error'}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* Attendance Tab */}
          {selectedTab === 2 && (
            <Box>
              {renderLeaderboardCards('attendance')}
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                Attendance Statistics
              </Typography>
              {attendanceData.total === 0 ? (
                <Alert severity="info">
                  Attendance tracking will be available soon. Admin will mark your attendance daily.
                </Alert>
              ) : (
                <>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                        <PieChart>
                          <Pie
                            data={attendancePieData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {attendancePieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Paper sx={{ p: 2, bgcolor: '#e8f5e9' }}>
                          <Typography variant="h4" fontWeight="bold" color="success.main">
                            {attendanceData.present}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Present Days
                          </Typography>
                        </Paper>
                        <Paper sx={{ p: 2, bgcolor: '#ffebee' }}>
                          <Typography variant="h4" fontWeight="bold" color="error.main">
                            {attendanceData.absent}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Absent Days
                          </Typography>
                        </Paper>
                        {attendanceData.late > 0 && (
                          <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
                            <Typography variant="h4" fontWeight="bold" color="warning.main">
                              {attendanceData.late}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Late Days
                            </Typography>
                          </Paper>
                        )}
                        <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                          <Typography variant="h4" fontWeight="bold" color="info.main">
                            {attendanceData.percentage.toFixed(1)}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Overall Attendance
                          </Typography>
                        </Paper>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>
          )}

          {/* Fees & Insights Tab */}
          {selectedTab === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                Fee Breakdown and Useful Insights
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 3, height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <WalletIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          Fee Summary
                        </Typography>
                      </Box>
                      <List dense disablePadding>
                        <ListItem disableGutters sx={{ py: 1 }}>
                          <ListItemText primary="Total Fee" secondary={formatCurrency(feeSummary.totalFee)} />
                        </ListItem>
                        <ListItem disableGutters sx={{ py: 1 }}>
                          <ListItemText primary="Paid Amount" secondary={formatCurrency(feeSummary.paidFee)} />
                        </ListItem>
                        <ListItem disableGutters sx={{ py: 1 }}>
                          <ListItemText
                            primary="Due Amount"
                            secondary={formatCurrency(feeSummary.dueFee)}
                            secondaryTypographyProps={{ color: feeSummary.dueFee > 0 ? 'error.main' : 'success.main', fontWeight: 700 }}
                          />
                        </ListItem>
                      </List>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Payment completion
                        </Typography>
                        <LinearProgress variant="determinate" value={feeSummary.paidPercent} sx={{ mt: 1, height: 8, borderRadius: 999 }} />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {feeSummary.paidPercent.toFixed(0)}% completed
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 3, height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <InsightsIcon color="secondary" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          Attendance Insights
                        </Typography>
                      </Box>
                      <List dense disablePadding>
                        <ListItem disableGutters sx={{ py: 1 }}>
                          <ListItemText primary="Days Present" secondary={attendanceInsights.present} />
                        </ListItem>
                        <ListItem disableGutters sx={{ py: 1 }}>
                          <ListItemText primary="Days Absent" secondary={attendanceInsights.absent} />
                        </ListItem>
                        <ListItem disableGutters sx={{ py: 1 }}>
                          <ListItemText primary="Late Days" secondary={attendanceInsights.late} />
                        </ListItem>
                        <ListItem disableGutters sx={{ py: 1 }}>
                          <ListItemText primary="Attendance Rate" secondary={`${attendanceInsights.attendancePercentage.toFixed(1)}%`} />
                        </ListItem>
                      </List>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Registration age
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {registrationDays} days
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 3, height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Recent Attendance
                      </Typography>
                      {recentAttendance.length === 0 ? (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          No attendance entries available yet.
                        </Alert>
                      ) : (
                        <List dense disablePadding>
                          {recentAttendance.map((entry) => (
                            <ListItem
                              key={`${entry.date}-${entry.status}`}
                              disableGutters
                              sx={{
                                py: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <ListItemText
                                primary={formatDate(entry.date)}
                                secondary={entry.remarks || 'No remarks'}
                              />
                              <Chip
                                size="small"
                                label={String(entry.status || '').toUpperCase()}
                                color={entry.status === 'present' ? 'success' : entry.status === 'late' ? 'warning' : 'error'}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 3, height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Useful Student Snapshot
                      </Typography>
                      <List dense disablePadding>
                        <ListItem disableGutters sx={{ py: 1 }}>
                          <ListItemText primary="Courses enrolled" secondary={enrolledCourses.length || 0} />
                        </ListItem>
                        <ListItem disableGutters sx={{ py: 1 }}>
                          <ListItemText primary="Days tracked" secondary={attendanceInsights.total || 0} />
                        </ListItem>
                        <ListItem disableGutters sx={{ py: 1 }}>
                          <ListItemText primary="Attendance status" secondary={attendanceInsights.attendancePercentage >= 75 ? 'Healthy' : 'Needs attention'} />
                        </ListItem>
                        <ListItem disableGutters sx={{ py: 1 }}>
                          <ListItemText primary="Fee status" secondary={feeSummary.isPaid ? 'Fully paid' : 'Pending dues'} />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 3, height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        What this page gives you
                      </Typography>
                      <List dense disablePadding>
                        <ListItem disableGutters sx={{ py: 1 }}>
                          <ListItemText primary="Current fee snapshot" secondary="Total, paid and pending dues in one place" />
                        </ListItem>
                        <ListItem disableGutters sx={{ py: 1 }}>
                          <ListItemText primary="Attendance health" secondary="Present, absent, late and overall percentage" />
                        </ListItem>
                        <ListItem disableGutters sx={{ py: 1 }}>
                          <ListItemText primary="Registration context" secondary={`${registrationDays} days since joining`} />
                        </ListItem>
                        <ListItem disableGutters sx={{ py: 1 }}>
                          <ListItemText primary="Progress tracking" secondary="Speed records, exam history and lesson progress remain below" />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Lesson Progress Tab */}
          {selectedTab === 4 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                Lesson Progress Report
              </Typography>

              {/* Course Selection */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Select Course</InputLabel>
                <Select
                  value={selectedCourseForProgress}
                  label="Select Course"
                  onChange={(e) => setSelectedCourseForProgress(e.target.value)}
                >
                  {enrolledCourses.map((courseCode) => {
                    const courseNames = {
                      'DCA': 'DCA - Diploma in Computer Applications',
                      'PGDCA': 'PGDCA - Post Graduate Diploma in Computer Applications',
                      'DTP': 'DTP - Desktop Publishing',
                      'TALLY': 'Tally',
                      'WEB_DESIGNING': 'Web Designing',
                    };
                    return (
                      <MenuItem key={courseCode} value={courseCode}>
                        {courseNames[courseCode] || courseCode}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              {lessonProgress && selectedCourseForProgress && lessonProgress[selectedCourseForProgress] ? (
                (() => {
                  const progress = lessonProgress[selectedCourseForProgress];
                  const pieData = [
                    { name: 'Completed', value: progress.completedLessons, color: '#4caf50' },
                    { name: 'Pending', value: progress.pendingLessons, color: '#ff9800' },
                  ];
                  const barData = [
                    { name: 'Completed', value: progress.completedLessons, fill: '#4caf50' },
                    { name: 'Pending', value: progress.pendingLessons, fill: '#ff9800' },
                  ];

                  return (
                    <>
                      {/* Summary Cards */}
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={3}>
                          <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon />
                                <Box>
                                  <Typography variant="h4" fontWeight="bold">
                                    {progress.completedLessons}
                                  </Typography>
                                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Completed
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PendingIcon />
                                <Box>
                                  <Typography variant="h4" fontWeight="bold">
                                    {progress.pendingLessons}
                                  </Typography>
                                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Pending
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <BookIcon />
                                <Box>
                                  <Typography variant="h4" fontWeight="bold">
                                    {progress.totalLessons}
                                  </Typography>
                                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Total Lessons
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TrendingUpIcon />
                                <Box>
                                  <Typography variant="h4" fontWeight="bold">
                                    {progress.completionPercentage}%
                                  </Typography>
                                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Progress
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>

                      {/* Progress Bar */}
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            Overall Progress
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {progress.completionPercentage}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={progress.completionPercentage}
                          sx={{
                            height: 10,
                            borderRadius: 5,
                            bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 5,
                              bgcolor: progress.completionPercentage >= 80 ? 'success.main' : 
                                       progress.completionPercentage >= 50 ? 'warning.main' : 'error.main',
                            },
                          }}
                        />
                      </Box>

                      {/* Charts */}
                      <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                          <Card>
                            <CardContent>
                              <Typography variant="h6" gutterBottom fontWeight="bold">
                                Completion Overview
                              </Typography>
                              <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                                <PieChart>
                                  <Pie
                                    data={pieData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, value, percent }) => 
                                      `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                                    }
                                    outerRadius={isMobile ? 80 : 100}
                                    fill="#8884d8"
                                    dataKey="value"
                                  >
                                    {pieData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                  </Pie>
                                  <Tooltip />
                                </PieChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Card>
                            <CardContent>
                              <Typography variant="h6" gutterBottom fontWeight="bold">
                                Lessons Comparison
                              </Typography>
                              <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                                <BarChart data={barData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" />
                                  <YAxis />
                                  <Tooltip />
                                  <Bar dataKey="value" radius={[8, 8, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>

                      {/* Completed Lessons List */}
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Card>
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <CheckCircleIcon color="success" />
                                <Typography variant="h6" fontWeight="bold">
                                  Completed Lessons ({progress.completedLessons})
                                </Typography>
                              </Box>
                              {progress.completed.length > 0 ? (
                                <List dense>
                                  {progress.completed.map((lesson, index) => (
                                    <ListItem key={index} sx={{ px: 0 }}>
                                      <ListItemIcon>
                                        <CheckCircleIcon color="success" fontSize="small" />
                                      </ListItemIcon>
                                      <ListItemText primary={lesson} />
                                    </ListItem>
                                  ))}
                                </List>
                              ) : (
                                <Alert severity="info">No lessons completed yet. Start learning!</Alert>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Card>
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <PendingIcon color="warning" />
                                <Typography variant="h6" fontWeight="bold">
                                  Pending Lessons ({progress.pendingLessons})
                                </Typography>
                              </Box>
                              {progress.pending.length > 0 ? (
                                <List dense>
                                  {progress.pending.map((lesson, index) => (
                                    <ListItem key={index} sx={{ px: 0 }}>
                                      <ListItemIcon>
                                        <PendingIcon color="warning" fontSize="small" />
                                      </ListItemIcon>
                                      <ListItemText primary={lesson} />
                                    </ListItem>
                                  ))}
                                </List>
                              ) : (
                                <Alert severity="success">🎉 All lessons completed! Great job!</Alert>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </>
                  );
                })()
              ) : (
                <Alert severity="info">
                  {selectedCourseForProgress 
                    ? 'No lesson progress data available for this course yet.'
                    : 'Please select a course to view lesson progress.'}
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
      </Box>
    </Box>
  );
};

export default StudentStatistics;




